import React from 'react';
import { motion, AnimatePresence, useDragControls } from 'framer-motion';
import TokenStack from './TokenStack';
import TokenIcon from './TokenIcon';
import ChainIcon from './ChainIcon';
import ProtocolIcon from './ProtocolIcon';
import AprHistoryChart from './AprHistoryChart';
import ShareButton from './ShareButton';
import { PROTOCOL_COLORS, RISK_MAP } from '../data';
import { fmt } from '../utils';
import { getPoolUrl, getSwapUrl } from '../poolUrls';
import { useTheme } from '../theme';
import { InfoIcon } from './Tooltip';
import TOOLTIP_CONTENT from '../tooltipContent';
import { useIsMobile } from '../useMediaQuery';

const METRIC_TOOLTIPS = {
  "TVL": TOOLTIP_CONTENT.TVL,
  "VOLUME 24H": TOOLTIP_CONTENT['VOLUME 24H'],
  "FEES 24H": TOOLTIP_CONTENT['FEES 24H'],
  "FEE TIER": TOOLTIP_CONTENT.FEE_TIER,
  "RISK": TOOLTIP_CONTENT.RISK,
};

export default function DetailPanel({ pool, onClose, onSwap, onViewDetail }) {
  const { colors } = useTheme();
  const isMobile = useIsMobile();
  const dragControls = useDragControls();

  const protocolColor = pool ? PROTOCOL_COLORS[pool.protocol] || colors.textPrimary : null;
  const risk = pool ? (RISK_MAP[pool.risk] || { color: '#888', bg: '#f0f0f0', border: '#ccc' }) : null;

  // Mobile bottom sheet config
  const mobileSheetHeight = '75vh';

  const handleDragEnd = (event, info) => {
    // If dragged down more than 100px, close
    if (info.offset.y > 100) {
      onClose();
    }
  };

  return (
    <AnimatePresence>
    {pool && (
      isMobile ? (
        // Mobile: Bottom Sheet
        <>
          {/* Backdrop */}
          <motion.div
            key="detail-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.4)',
              zIndex: 99,
            }}
          />
          <motion.div
            key="detail-sheet"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            drag="y"
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.6 }}
            dragControls={dragControls}
            dragListener={false}
            onDragEnd={handleDragEnd}
            style={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              height: mobileSheetHeight,
              background: colors.surface,
              borderRadius: '16px 16px 0 0',
              boxShadow: '0 -8px 32px rgba(0,0,0,0.15)',
              zIndex: 100,
              overflowY: 'auto',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            {/* Drag handle — only this area triggers drag */}
            <div
              onPointerDown={(e) => dragControls.start(e)}
              style={{
                display: 'flex',
                justifyContent: 'center',
                padding: '10px 0 6px',
                position: 'sticky',
                top: 0,
                background: colors.surface,
                zIndex: 1,
                cursor: 'grab',
                touchAction: 'none',
              }}
            >
              <div style={{
                width: 36,
                height: 4,
                borderRadius: 2,
                background: colors.labelLighter,
              }} />
            </div>

            {renderPanelContent({ pool, onClose, onSwap, onViewDetail, protocolColor, risk, colors, isMobile: true })}
          </motion.div>
        </>
      ) : (
        // Desktop: Side Panel
        <motion.div
          key="detail-panel"
          initial={{ x: "100%" }}
          animate={{ x: 0 }}
          exit={{ x: "100%" }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          style={{
            position: "fixed",
            top: 0,
            right: 0,
            width: 370,
            height: "100dvh",
            background: colors.surface,
            boxShadow: `-12px 0 40px ${colors.panelShadow}`,
            zIndex: 100,
            overflowY: "auto",
          }}
        >
          {renderPanelContent({ pool, onClose, onSwap, onViewDetail, protocolColor, risk, colors, isMobile: false })}
        </motion.div>
      )
    )}
    </AnimatePresence>
  );
}

function renderPanelContent({ pool, onClose, onSwap, onViewDetail, protocolColor, risk, colors, isMobile }) {
  return (
    <>
      {/* Header */}
      <div style={{
        padding: "20px 24px 16px",
        borderBottom: `1px solid ${colors.borderLight}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <TokenStack tokens={pool.tokens || []} size={32} />
          <div>
            <div style={{
              fontFamily: "'DM Serif Display', serif",
              fontSize: 18,
              color: colors.textPrimary,
            }}>
              {pool.name}
            </div>
            <div style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 10,
              color: colors.labelMuted,
              marginTop: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 3,
            }}>
              <ProtocolIcon protocol={pool.protocol} size={12} /> {pool.protocol} · <ChainIcon chain={pool.chain} size={12} /> {pool.chain}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {!isMobile && (
            <button onClick={onClose} style={{
              background: "none",
              border: "none",
              fontSize: 20,
              color: colors.labelClose,
              cursor: "pointer",
              padding: 4,
            }}>
              ✕
            </button>
          )}
        </div>
      </div>

      {/* APR Hero */}
      <div style={{
        padding: "24px 24px",
        textAlign: "center",
        borderBottom: `1px solid ${colors.borderLight}`,
      }}>
        <div style={{
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 10,
          color: colors.labelLight,
          letterSpacing: 1.5,
          marginBottom: 8,
        }}>
          ANNUAL PERCENTAGE RATE
        </div>
        <div style={{
          fontFamily: "'DM Serif Display', serif",
          fontSize: 48,
          color: (pool.apr || 0) > 20 ? colors.gold : colors.green,
        }}>
          {(pool.apr || 0).toFixed(2)}%
        </div>
      </div>

      {/* Metrics */}
      <div style={{ padding: "20px 24px", borderBottom: `1px solid ${colors.borderLight}` }}>
        {[
          { label: "TVL", value: fmt(pool.tvl) },
          { label: "VOLUME 24H", value: fmt(pool.volume24h) },
          { label: "FEES 24H", value: fmt(pool.fees24h) },
          { label: "FEE TIER", value: pool.fee },
          { label: "RISK", value: pool.risk },
        ].map(m => (
          <div key={m.label} style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "10px 0",
            borderBottom: `1px solid ${colors.borderSubtle}`,
          }}>
            <span style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 10,
              color: colors.labelLight,
              letterSpacing: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 5,
            }}>
              {m.label}
              {METRIC_TOOLTIPS[m.label] && (
                <InfoIcon text={METRIC_TOOLTIPS[m.label]} position="right" colors={colors} />
              )}
            </span>
            {m.label === "RISK" ? (
              <span style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 9,
                fontWeight: 600,
                color: risk.color,
                background: risk.bg,
                border: `1px solid ${risk.border}`,
                padding: "2px 8px",
                borderRadius: 3,
              }}>
                {m.value}
              </span>
            ) : (
              <span style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 12,
                color: colors.textSecondary,
                fontWeight: 500,
              }}>
                {m.value}
              </span>
            )}
          </div>
        ))}
      </div>

      {/* Tokens */}
      <div style={{ padding: "20px 24px", borderBottom: `1px solid ${colors.borderLight}` }}>
        <div style={{
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 10,
          color: colors.labelLight,
          letterSpacing: 1,
          marginBottom: 12,
        }}>
          TOKENS
        </div>
        {(pool.tokens || []).map(t => (
          <div key={t} style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "8px 0",
          }}>
            <TokenIcon symbol={t} size={24} />
            <span style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 13,
              color: colors.textPrimary,
              fontWeight: 500,
            }}>
              {t}
            </span>
          </div>
        ))}
      </div>

      {/* APR Breakdown */}
      {(pool.apyBase > 0 || pool.apyReward > 0) && (
        <div style={{ padding: "16px 24px", borderBottom: `1px solid ${colors.borderLight}` }}>
          <div style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 10,
            color: colors.labelLight,
            letterSpacing: 1,
            marginBottom: 10,
          }}>
            APR BREAKDOWN
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", alignItems: "center" }}>
            <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: colors.textSecondary, display: 'flex', alignItems: 'center', gap: 5 }}>
              Base APY
              <InfoIcon text={TOOLTIP_CONTENT.BASE_APY} position="right" colors={colors} />
            </span>
            <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: colors.green, fontWeight: 500 }}>
              {(pool.apyBase || 0).toFixed(2)}%
            </span>
          </div>
          {pool.apyReward > 0 && (
            <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", alignItems: "center" }}>
              <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: colors.textSecondary, display: 'flex', alignItems: 'center', gap: 5 }}>
                Reward APY
                <InfoIcon text={TOOLTIP_CONTENT.REWARD_APY} position="right" colors={colors} />
              </span>
              <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: colors.gold, fontWeight: 500 }}>
                +{(pool.apyReward || 0).toFixed(2)}%
              </span>
            </div>
          )}
        </div>
      )}

      {/* APR History Chart */}
      <AprHistoryChart pool={pool} />

      {/* CTA Buttons */}
      <div style={{ padding: "24px", display: "flex", flexDirection: "column", gap: 10 }}>
        {/* View full detail page */}
        {onViewDetail && (
          <button
            onClick={onViewDetail}
            style={{
              display: "block",
              width: "100%",
              padding: "12px 0",
              borderRadius: 8,
              border: `1.5px solid ${colors.green}`,
              background: colors.greenBg,
              color: colors.green,
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: 0.5,
              cursor: "pointer",
              textAlign: "center",
            }}
          >
            View Full Details \u2192
          </button>
        )}
        {pool.poolId && (
        <a
          href={`https://defillama.com/yields/pool/${pool.poolId}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: "block",
            width: "100%",
            padding: "14px 0",
            borderRadius: 8,
            border: "none",
            background: "#1a5276",
            color: "#FFFFFF",
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
            textAlign: "center",
            textDecoration: "none",
            boxSizing: "border-box",
          }}
        >
          🦙 View Pool on DeFi Llama ↗
        </a>
        )}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
        }}>
          <span style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 10,
            color: "#AAAAAA",
          }}>
            or add liquidity directly:
          </span>
          <ShareButton pool={pool} colors={colors} size="small" />
        </div>
        {getPoolUrl(pool) && (
        <a
          href={getPoolUrl(pool)}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: "block",
            width: "100%",
            padding: "14px 0",
            borderRadius: 8,
            border: "none",
            background: protocolColor,
            color: "#FFFFFF",
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: 0.5,
            cursor: "pointer",
            textAlign: "center",
            textDecoration: "none",
            boxSizing: "border-box",
          }}
        >
          Add Liquidity on {pool.protocol} ↗
        </a>
        )}
        {getSwapUrl(pool) && (
        <a
          href={getSwapUrl(pool)}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: "block",
            width: "100%",
            padding: "12px 0",
            borderRadius: 8,
            border: `1.5px solid ${protocolColor}`,
            background: "transparent",
            color: protocolColor,
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: 0.5,
            cursor: "pointer",
            textAlign: "center",
            textDecoration: "none",
            boxSizing: "border-box",
          }}
        >
          Swap on {pool.protocol} ↗
        </a>
        )}
        {onSwap && pool.tokens && pool.tokens.length >= 2 && (
        <button
          onClick={() => onSwap(pool.tokens[0], pool.tokens[1])}
          style={{
            display: "block",
            width: "100%",
            padding: "12px 0",
            borderRadius: 8,
            border: `1.5px solid ${colors.green}`,
            background: colors.greenBg,
            color: colors.green,
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: 0.5,
            cursor: "pointer",
            textAlign: "center",
            boxSizing: "border-box",
          }}
        >
          Swap {pool.tokens[0]} ↔ {pool.tokens[1]} on PoolScope
        </button>
        )}
      </div>
    </>
  );
}
